import Vue from 'vue';
import { useOneReachConfig } from '@/composables';

export const useComponentBundles = () => {
  const HITL_BUNDLE_PREFIX = 'HITL';
  const HITL_UI_SERVICE = HITL_BUNDLE_PREFIX.toLowerCase();
  const BUNDLE_DATA_ATTR = 'data-bundle';
  const BUNDLE_DEV_VERSION = 'HITL-Dev';
  const BUNDLE_PACKAGES = 'packages.umd.min.js';
  const BUNDLE_STYLES = 'styles.min.css';
  const LOCALHOST = 'http://localhost:5500/';

  const ORConfig = useOneReachConfig().getORConfig();

  /**
   * Bundle IDs are UMD module exports provided by webpack like "agent-ui".
   * Bundle names are main bundle scripts like "agent-ui.umd.min.js".
   * TODO: UMD bundle expects dependencies to be in global "window" and injecting script puts component there as well.
   */
  const getComponentBundle = async (args: {
    bundleId: string;
    bundleName: string;
    version: string;
    discoveryUrl: string;
    isDevMode?: boolean;
  }) => {
    try {
      const { bundleId, bundleName, version, discoveryUrl, isDevMode } = args;
      const { url } = await getDiscoveryURL({ discoveryUrl });

      (window as any).Vue = Vue;
      const bundlePath = `${url}/core/v${version}/`;

      /* TODO: Use "isDevMode: true" to load local bundle */
      await createBundleScripts({
        bundlePath,
        bundleId,
        bundleName,
        version,
        isDevMode,
      });

      const component = (window as any)[bundleId].default;
      delete (window as any)[bundleId];
      return component;
    } catch (e) {
      console.debug('Error while retrieving component bundle!', e);
      throw e;
    }
  };

  const getBundleKey = (args: { key: string; version: string; }) => {
    return `${HITL_BUNDLE_PREFIX}-${args.key}-v${args.version}`;
  };

  /** In most cases we should have UI URL in the config so we won't have to make a request */
  const getDiscoveryURL = async (args: {
    discoveryUrl: string;
  }): Promise<{ url: string; }> => {
    if (ORConfig.HITL_UI_URL) {
      return { url: ORConfig.HITL_UI_URL };
    }

    const url = `${args.discoveryUrl}/api/v2?serviceName=${HITL_UI_SERVICE}`;
    const response = await fetch(url);
    return await response.json();
  };

  /**
   * Create all scripts and styles necessary for bundle root component to work.
   * Order of scripts is important!
   * Bundle Path is the same as Webpack Public Path and should end with "/".
   * TODO: There might be a more complex dependency graph in the future?
   * TODO: Provide "isDevMode" using query param!
   */
  const createBundleScripts = async (args: {
    bundlePath: string;
    bundleId: string;
    bundleName: string;
    version?: string;
    isDevMode?: boolean;
  }) => {
    const { bundlePath, bundleId, bundleName, isDevMode, version = BUNDLE_DEV_VERSION } = args;
    const bundleKey = getBundleKey({ key: bundleId, version });
    
    const STYLE_BUNDLES = [`${bundlePath}${BUNDLE_STYLES}`];
    const APP_BUNDLES = [
      `${bundlePath}${BUNDLE_PACKAGES}`,
      `${bundlePath}${bundleName}`,
    ];
    const DEV_BUNDLES = [
      `${LOCALHOST}${BUNDLE_PACKAGES.replace('.min', '')}`,
      `${LOCALHOST}${bundleName.replace('.min', '')}`,
    ];

    /* We have to provide public path to bundles so they can request their chunks */
    window.HITL_UI_PUBLIC_PATH = isDevMode ? LOCALHOST : bundlePath;

    const getScriptsInSequence = async (sources: string[]) => {
      for (const src of sources) {
        await getScript(src);
      }
    }

    /** TODO: Why can't we sequence loading using "script.src" and "script.onload"? */
    const getScript = async (src: string) => {
      try {
        const script = document.createElement('script');
        const response = await fetch(src, { mode: 'cors' });
        script.setAttribute(BUNDLE_DATA_ATTR, bundleKey);
        script.crossOrigin = 'anonymous';
        script.defer = true;
        script.text = await response.text();
        document.head.appendChild(script);
      } catch (e) {
        console.warn('[App Launcher] Error while requesting component bundle!', e);
      }
    };

    const getStyle = (src: string) => new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.setAttribute(BUNDLE_DATA_ATTR, bundleKey)
      style.href = src;
      style.rel = 'stylesheet';
      style.onload = resolve;
      style.onerror = (e: any) => {
        console.warn('[App Launcher] Error while requesting component bundle!', e);
        resolve('');
      };
      document.head.appendChild(style);
    });

    /* Content should be requested using "CORS" due to Content Security Policy */
    if (isDevMode) {
      await getScriptsInSequence(DEV_BUNDLES);
    } else {
      await Promise.all([
        getScriptsInSequence(APP_BUNDLES),
        ...STYLE_BUNDLES.map(src => getStyle(src)),
      ]);
    }
  };

  /** Delete all injected scripts and styles and reset Webpack Public Path */
  const cleanUpBundles = () => {
    window.HITL_UI_PUBLIC_PATH = '';
    window.webpackChunkHITLApps = [];
    const query = `[${BUNDLE_DATA_ATTR}]`;
    const elements = document.querySelectorAll(query);
    elements.forEach(el => el.remove());
  };

  return { getComponentBundle, getBundleKey, cleanUpBundles };
};
